/**
 * Image editing tools (§19).
 *
 * Every action appends to the model's edit list rather than mutating pixels, so
 * undo is exact and the decoded original is never touched (§13). Concealment
 * tools take a region, which the user draws on the canvas — a mosaic applied to
 * the whole image would be useless, and one applied to a guessed region would
 * be worse.
 */
import { useCallback, useRef, useState } from 'react';
import { applyEdit, canRedo, canUndo, redo, undo } from '@moduviewer/handlers';
import type { ImageModel } from '@moduviewer/handlers';
import type { Rect } from '@moduviewer/core';
import { Icon } from '@moduviewer/design/icon';
import type { IconName } from '@moduviewer/design/icon';

export type RegionTool = 'crop' | 'mosaic' | 'blur' | null;

export interface ImageToolsProps {
  readonly model: ImageModel;
  readonly tool: RegionTool;
  readonly onTool: (tool: RegionTool) => void;
  readonly onChange: (next: ImageModel) => void;
}

export function ImageTools(
  { model, tool, onTool, onChange }: ImageToolsProps,
): React.JSX.Element {
  const apply = useCallback((edit: Parameters<typeof applyEdit>[1]) => {
    onChange(applyEdit(model, edit));
  }, [model, onChange]);

  return (
    <div className="toolbar" role="toolbar" aria-label="이미지 편집 도구">
      <Tool icon="undo" label="실행 취소" disabled={!canUndo(model)} onClick={() => onChange(undo(model))} />
      <Tool icon="redo" label="다시 실행" disabled={!canRedo(model)} onClick={() => onChange(redo(model))} />
      <span className="sep" />

      <Tool icon="rotate" label="오른쪽으로 회전" onClick={() => apply({ kind: 'rotate', degrees: 90 })} />
      <Tool
        icon="fit-width" label="좌우 반전"
        onClick={() => apply({ kind: 'flip', axis: 'horizontal' })}
      />
      <span className="sep" />

      <Tool icon="crop" label="자르기" active={tool === 'crop'} onClick={() => onTool(tool === 'crop' ? null : 'crop')} />
      <Tool icon="mosaic" label="모자이크" active={tool === 'mosaic'} onClick={() => onTool(tool === 'mosaic' ? null : 'mosaic')} />
      <Tool icon="eraser" label="흐리게" active={tool === 'blur'} onClick={() => onTool(tool === 'blur' ? null : 'blur')} />
      <span className="sep" />

      <Tool icon="adjust" label="흑백" onClick={() => apply({ kind: 'grayscale' })} />

      <span className="spacer" />
      {tool ? (
        <span className="badge badge--accent">
          {tool === 'crop' ? '자를 영역을 드래그하세요'
            : tool === 'mosaic' ? '가릴 영역을 드래그하세요'
              : '흐리게 할 영역을 드래그하세요'}
        </span>
      ) : null}
    </div>
  );
}

function Tool(
  { icon, label, onClick, active, disabled }: {
    icon: IconName; label: string; onClick: () => void;
    active?: boolean; disabled?: boolean;
  },
): React.JSX.Element {
  return (
    <button
      className="btn btn--icon tip"
      data-tip={label}
      aria-label={label}
      aria-pressed={active ?? undefined}
      disabled={disabled}
      onClick={onClick}
    >
      <Icon name={icon} />
    </button>
  );
}

/**
 * Drag-to-select overlay.
 *
 * Coordinates are converted from screen space to image space on release, so a
 * selection means the same pixels whatever the zoom — a rectangle recorded in
 * CSS pixels would silently crop the wrong region at anything but 100%.
 */
export function RegionSelector(
  { tool, width, height, displayWidth, displayHeight, onSelect }: {
    tool: RegionTool;
    width: number; height: number;
    displayWidth: number; displayHeight: number;
    onSelect: (rect: Rect) => void;
  },
): React.JSX.Element | null {
  const host = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);

  if (!tool) return null;

  const toImage = (rect: { x0: number; y0: number; x1: number; y1: number }): Rect => {
    const sx = width / displayWidth;
    const sy = height / displayHeight;
    return {
      x: Math.min(rect.x0, rect.x1) * sx,
      y: Math.min(rect.y0, rect.y1) * sy,
      width: Math.abs(rect.x1 - rect.x0) * sx,
      height: Math.abs(rect.y1 - rect.y0) * sy,
    };
  };

  const local = (e: React.PointerEvent): { x: number; y: number } => {
    const box = host.current!.getBoundingClientRect();
    return { x: e.clientX - box.left, y: e.clientY - box.top };
  };

  return (
    <div
      ref={host}
      className="region"
      style={{ width: displayWidth, height: displayHeight }}
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        const p = local(e);
        setDrag({ x0: p.x, y0: p.y, x1: p.x, y1: p.y });
      }}
      onPointerMove={(e) => {
        if (!drag) return;
        const p = local(e);
        setDrag({ ...drag, x1: p.x, y1: p.y });
      }}
      onPointerUp={() => {
        if (!drag) return;
        const rect = toImage(drag);
        setDrag(null);
        // A stray click is not a selection.
        if (rect.width >= 4 && rect.height >= 4) onSelect(rect);
      }}
    >
      {drag ? (
        <div
          className="region__box"
          style={{
            left: Math.min(drag.x0, drag.x1),
            top: Math.min(drag.y0, drag.y1),
            width: Math.abs(drag.x1 - drag.x0),
            height: Math.abs(drag.y1 - drag.y0),
          }}
        />
      ) : null}
    </div>
  );
}
